import { AudioContextClock, loadAudioBuffer } from '@ezchops/clock';
import range from 'lodash/range';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAudioContext } from '../../contexts';
import { SamplePlayer } from '../soundfont';
export function useSequencerClock(config, autoStart = false) {
    const audioContext = useAudioContext();
    const clock = useMemo(() => new AudioContextClock(audioContext), [audioContext]);
    const [sequencer, setSequencer] = useState(new Sequencer({}, [], audioContext, clock));
    const updateSequencer = useCallback((sequencer) => {
        setSequencer(old => {
            old.close();
            return sequencer;
        });
    }, []);
    useEffect(() => {
        updateSequencer(new Sequencer({}, [], audioContext, clock));
        let running = true;
        const job = Sequencer.load(config?.samples ?? {}, config?.steps ?? [], audioContext, clock);
        job.then(sequencer => {
            if (running)
                updateSequencer(sequencer);
        }).catch(e => console.error(e));
        return () => {
            running = false;
            job.then(sequencer => sequencer.close());
        };
    }, [audioContext, autoStart, clock, config, updateSequencer]);
    return { sequencer, clock };
}
export function useSequencer(samples, steps, clock) {
    const audioContext = useAudioContext();
    const [sequencer, setSequencer] = useState();
    useEffect(() => {
        const job = Sequencer.load(samples, steps, audioContext, clock);
        job.then(setSequencer).catch(e => console.error(e));
        return () => {
            job.then(sequencer => sequencer.close());
        };
    }, [audioContext, clock, samples, steps]);
    return sequencer;
}
export class Sequencer extends SamplePlayer {
    buffers;
    steps;
    clock;
    scheduled;
    interval;
    constructor(buffers, steps, audioContext, clock) {
        super(audioContext);
        this.buffers = buffers;
        this.steps = steps;
        this.clock = clock;
        this.buffers = buffers;
        this.scheduled = new Map();
        this.clock.addEventListener('seeked', this.cancelAll);
        this.clock.addEventListener('paused', this.cancelAll);
        this.clock.addEventListener('resumed', this.scheduleNext);
        this.clock.addEventListener('reset', this.cancelAll);
        if (steps.length > 0) {
            this.interval = window.setInterval(this.scheduleNext(50), 25);
        }
    }
    get isEmpty() {
        return this.steps.length == 0;
    }
    /**
     * Generate a waveform for the sequencer
     * @param resolution in seconds
     */
    waveform = (resolution) => {
        if (this.steps.length == 0) {
            return [];
        }
        const stepWaveForms = this.steps.map(({ sample, when }) => {
            const buffer = this.buffers[sample];
            return bufferWaveform(buffer, when, resolution);
        });
        const duration = Math.max(...stepWaveForms.map(({ timePieces, startStep }) => startStep + timePieces.length));
        const waveform = range(0, duration).map(() => [0, 0]);
        stepWaveForms.forEach(({ timePieces, startStep }) => {
            timePieces.forEach((piece, index) => {
                const [min, max] = piece;
                const [currentMin, currentMax] = waveform[startStep + index];
                waveform[startStep + index] = [Math.min(min, currentMin), Math.max(max, currentMax)];
            });
        });
        return waveform;
    };
    scheduleNext = (lookAhead) => () => {
        const now = this.clock.now;
        const nextStepTime = now + lookAhead / 1000;
        if (this.clock.isRunning) {
            this.steps.forEach(({ when, sample }, index) => {
                const notScheduled = !this.scheduled.has(index);
                if (notScheduled) {
                    const notInPast = now <= when + this.duration(sample);
                    const comingSoon = when <= nextStepTime;
                    if (notInPast && comingSoon) {
                        this.schedule(index);
                    }
                }
            });
        }
    };
    duration = (sample) => {
        return this.buffers[sample].duration;
    };
    close() {
        this.cancelAll();
        this.clock.removeEventListener('seeked', this.cancelAll);
        this.clock.removeEventListener('reset', this.cancelAll);
        this.clock.addEventListener('resumed', this.scheduleNext);
        this.clock.addEventListener('reset', this.cancelAll);
        window.clearInterval(this.interval);
    }
    cancelAll = () => {
        this.scheduled.forEach((node, stepIndex) => node.close());
        this.scheduled.clear();
    };
    schedule = (stepIndex) => {
        const isPlaying = this.scheduled.has(stepIndex);
        if (!isPlaying) {
            const { sample, when } = this.steps[stepIndex];
            const audioNow = this.now;
            const trackNow = this.clock.now;
            const within = when - trackNow;
            const node = this.play(sample, audioNow + Math.max(0, within), Math.max(0, -within));
            if (node) {
                this.scheduled.set(stepIndex, node);
            }
        }
    };
    play = (sample, when, offset, gain) => {
        if (!(sample in this.buffers)) {
            console.warn(`No sample ${sample} in sequencer`, 'warning');
        }
        return this._play(this.buffers[sample], when, gain, offset);
    };
    static async load(samples, steps, audioContext, clock) {
        const buffers = await Sequencer.loadSamples(samples, audioContext);
        return new Sequencer(buffers, steps, audioContext, clock);
    }
    static async loadSamples(samples, audioContext) {
        const buffers = await Promise.all(Object.entries(samples).map(([sample, url]) => loadAudioBuffer(url, audioContext)
            .then((buffer) => [sample, buffer])));
        return Object.fromEntries(buffers);
    }
}
export function bufferWaveform(buffer, start, resolution) {
    const duration = buffer.duration;
    const leftChannel = buffer.getChannelData(0);
    const rate = buffer.sampleRate; // samples / second
    const resolutionInSamples = resolution * rate;
    const startStep = Math.round(start * rate / resolutionInSamples);
    const durationInSamples = Math.floor(duration * rate / resolutionInSamples) * resolutionInSamples;
    const timePieces = range(0, durationInSamples, resolutionInSamples).map((offset) => {
        let sliceMax = 0;
        let sliceMin = 0;
        const sliceStart = Math.floor(Math.max(0, offset));
        const sliceEnd = Math.ceil(Math.min(offset + resolutionInSamples, leftChannel.length));
        for (let i = sliceStart; i < sliceEnd; i++) {
            const value = leftChannel[i];
            sliceMax = Math.max(sliceMax, value);
            sliceMin = Math.min(sliceMin, value);
        }
        return [sliceMin, sliceMax];
    });
    return { startStep, timePieces };
}
//# sourceMappingURL=Sequencer.js.map